import { LazyQueryExecFunction } from '@apollo/client'
import {
  ActionData,
  BUTTON_NODES_COLUMN_NAME,
  CellInput,
  convertCsvInputToCell,
  CsvConversionContext,
  DossDataFormat,
  formatCurrency,
  formatDate,
  formatDateTimeByLocale,
  FormFieldData,
  getColumnsForCsvUpload,
  getUserVisibleIndexValue,
  isBooleanString,
  isLocaleCode,
  isResolvedTableCellData,
  joinSortedArrayElements,
  LocaleCodeMap,
  PARENT_ROW_ID_COLUMN_NAME,
  parseNumberByLocale,
  parseNumberWithUnknownLocale,
  parseSystemColumnFieldId,
  RECORD_ID_COLUMN_NAME,
  TableCellData,
  TableColumnForTableExport,
  toDateTimeString,
} from '@doss/common-all'
import { SystemGeneratedTableColumnType } from '@doss/prisma/types'
import dayjs from 'dayjs'
import FileSaver from 'file-saver'
import { orderBy, trim } from 'lodash'
import Papa from 'papaparse'
import { v4 as uuidv4 } from 'uuid'
import {
  Currency,
  DataType,
  Exact,
  FormatPreset,
  FormFieldType,
  GetCsvExportTableRowsQuery,
  IndexType,
  Locale,
  PaginatedCsvExportInput,
} from '~/common/generated/apollo-operations'
import { DetailedFormField } from '~/components/forms/types'
import { CsvColumn, CsvTable, DetailedTableTab, TableType } from '~/components/tables/types'
import {
  getFiltersFromTableTab,
  swapLoggedInUserFilterArgForUserId,
} from '../../components/tables/filters'
import { getSortsFromTableTab } from '../../components/tables/sorts'
import { TranslationKeys } from '../generated/i18n.generated'

const FETCH_NUM_ROWS = 125

export interface TableCsvExportOptions {
  id: string
  name: string
  selectedColumns: TableColumnForTableExport[]
  allColumns: TableColumnForTableExport[]
  childTableColumns?: TableColumnForTableExport[]
  rowCount: number
  handleCloseDialog?: () => void
  exportPath: string
  tableTabForExport?: DetailedTableTab
  parentRowId?: string
  parentTableColumns?: TableColumnForTableExport[]
  backlinkColumnId?: string
  backlinkRowId?: string
}

// Define the expected structure of a row data based on your CSV format
interface CsvRowData {
  [key: string]: string | number
}

interface PapaParseError {
  message: string
  type?: string
  code?: string
}

/**
 * Creates a CSV with the table column headers and rows and downloads it
 */
export async function createAndDownloadTableCsv(
  table: TableCsvExportOptions,
  includeRecordId: boolean,
  getPaginatedTableData: LazyQueryExecFunction<
    GetCsvExportTableRowsQuery,
    Exact<{
      input: PaginatedCsvExportInput
    }>
  >,
  setProgress: (progress: number) => void,
  signal: AbortSignal,
  locale: Locale,
  timeZone: string,
  currency: Currency,
  tableType: TableType,
  currentUserId?: string,
): Promise<void> {
  try {
    let hasNext = true
    let offset = 0

    const showParentRecordIdColumn = tableType === TableType.CHILD_TABLE

    const columnHeadersOrdered = orderBy(
      table.selectedColumns,
      (column) => column.order,
      'asc',
    ).map((header) => header.name)

    let headers: string[]
    if (includeRecordId && showParentRecordIdColumn) {
      headers = [RECORD_ID_COLUMN_NAME, PARENT_ROW_ID_COLUMN_NAME, ...columnHeadersOrdered]
    } else if (includeRecordId) {
      headers = [RECORD_ID_COLUMN_NAME, ...columnHeadersOrdered]
    } else if (showParentRecordIdColumn) {
      headers = [PARENT_ROW_ID_COLUMN_NAME, ...columnHeadersOrdered]
    } else {
      headers = columnHeadersOrdered
    }

    // Conditionally include parent row headers
    if (table.parentTableColumns) {
      headers.push(
        ...orderBy(table.parentTableColumns, (column) => column.order, 'asc').map(
          (header) => header.name,
        ),
      )
    }

    // When exporting child tables with parent filters, use parent table columns for filter matching
    // When exporting parent tables with child table filters, include child table columns
    let columnsForFiltering: TableColumnForTableExport[]
    if (table.parentTableColumns && table.tableTabForExport) {
      // Exporting a child table - use parent and child table columns for filter matching
      columnsForFiltering = table.childTableColumns
        ? [...table.parentTableColumns, ...table.childTableColumns]
        : table.parentTableColumns
    } else {
      // Exporting a parent or regular table - include both table columns and child table columns
      columnsForFiltering = table.allColumns
      if (table.childTableColumns) {
        columnsForFiltering = [...table.allColumns, ...table.childTableColumns]
      }
    }
    const { cleanedFilters } = getFiltersFromTableTab({
      selectedTab: table.tableTabForExport,
      tableColumns: columnsForFiltering,
      tableId: table.id,
    })

    const effectiveFilters = swapLoggedInUserFilterArgForUserId(cleanedFilters, currentUserId)

    const { cleanedSorts } = getSortsFromTableTab(table.tableTabForExport)

    const filterBooleanOperator = table.tableTabForExport?.booleanOperator

    const rows: string[][] = []
    while (hasNext) {
      if (signal.aborted) {
        return
      }

      const { data } = await getPaginatedTableData({
        variables: {
          input: {
            tableId: table.id,
            pagination: { limit: FETCH_NUM_ROWS, offset },
            filters: effectiveFilters,
            sorts: cleanedSorts,
            filterBooleanOperator,
            parentRowId: table.parentRowId,
            backlinkColumnId: table.backlinkColumnId,
            backlinkRowId: table.backlinkRowId,
            tableTabId: table.tableTabForExport?.id,
            columnIds: table.selectedColumns.map((column) => column.id),
            exportChildWithParentRow: tableType === TableType.CHILD_TABLE,
          },
        },
      })

      if (!data) {
        throw new Error('No data returned from query')
      }
      const paginatedRows = data.paginatedCsvExport.rows

      const rowsData = paginatedRows.flatMap((row) => {
        if (table.parentTableColumns) {
          // If parent table columns are present, this is a child table. Include parent row data
          // appended to the child row data
          const parentRowData = getRowData(
            table.parentTableColumns,
            row,
            locale,
            timeZone,
            currency,
          )

          const childRowsData = row.childRows.map((childRow) => {
            const childRowData = getRowData(
              table.selectedColumns,
              childRow,
              locale,
              timeZone,
              currency,
            )
            let rowData = [...childRowData, ...parentRowData]
            if (includeRecordId && showParentRecordIdColumn) {
              rowData = [childRow.id, row.id, ...childRowData, ...parentRowData]
            } else if (includeRecordId) {
              rowData = [childRow.id, ...childRowData, ...parentRowData]
            } else if (showParentRecordIdColumn) {
              rowData = [row.id, ...childRowData, ...parentRowData]
            }
            return rowData
          })

          return childRowsData
        } else {
          const cellValues = getRowData(table.selectedColumns, row, locale, timeZone, currency)
          let rowData: string[]
          if (includeRecordId && showParentRecordIdColumn) {
            const parentId = row.parentRow?.id ?? ''
            rowData = [row.id, parentId, ...cellValues]
          } else if (includeRecordId) {
            rowData = [row.id, ...cellValues]
          } else if (showParentRecordIdColumn) {
            const parentId = row.parentRow?.id ?? ''
            rowData = [parentId, ...cellValues]
          } else {
            rowData = cellValues
          }
          return [rowData]
        }
      })
      rows.push(...rowsData)
      offset = data.paginatedCsvExport.offset
      hasNext = data.paginatedCsvExport.hasNext
      setProgress((offset / data.paginatedCsvExport.totalCount) * 100)
    }

    const csv = Papa.unparse([headers, ...rows], { header: false })
    // Adding BOM character to front so Excel can detect encoding
    // https://stackoverflow.com/questions/19492846/javascript-to-csv-export-encoding-issue
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const now = dayjs().format('YYYY-MM-DD')
    const saveAsFilePath = `${table.exportPath}-${now}.csv`
    FileSaver.saveAs(blob, saveAsFilePath, { autoBom: false })
  } catch (error) {
    throw new Error(error.message)
  }
}

function getRowData(
  columns: Pick<
    TableCsvExportOptions['selectedColumns'][number],
    'id' | 'order' | 'formatPreset' | 'lookupColumn' | 'type'
  >[],
  row: Omit<GetCsvExportTableRowsQuery['paginatedCsvExport']['rows'][number], 'childRows'>,
  locale: Locale,
  timeZone: string,
  currency: Currency,
): string[] {
  const rowData = orderBy(columns, (column) => column.order, 'asc').map((header) => {
    // If the header is a system-generated column, use the system-generated column type to get the cell
    const systemGeneratedColumnType = parseSystemColumnFieldId(header.id) ? header.id : null

    if (systemGeneratedColumnType) {
      switch (systemGeneratedColumnType) {
        case SystemGeneratedTableColumnType.CREATED_AT:
          return formatDateTimeByLocale(row.createdAt, LocaleCodeMap[locale], timeZone)
        case SystemGeneratedTableColumnType.UPDATED_AT:
          return formatDateTimeByLocale(row.updatedAt, LocaleCodeMap[locale], timeZone)
        case SystemGeneratedTableColumnType.CREATED_BY: {
          const createdBy = row.createdBy
          if (!createdBy) {
            return ''
          }
          return `${createdBy.firstName} ${createdBy.lastName}`
        }
        case SystemGeneratedTableColumnType.UPDATED_BY: {
          const updatedBy = row.updatedBy
          if (!updatedBy) {
            return ''
          }
          return `${updatedBy.firstName} ${updatedBy.lastName}`
        }
      }
    }
    const cell = row.cells.find((cell) => cell.column.id === header.id)
    if (!cell || !cell.data) {
      return ''
    }
    const data = cell.data

    // Formula cells have a null value when not yet computed (or computed to
    // null), which fails the resolved-cell schema check below since it requires a non-null
    // number. Treat that the same as "no cell value" rather than an invalid-data error.
    if (
      (data.type === DossDataFormat.FORMULA ||
        data.type === DossDataFormat.FORMULA_NUMBER ||
        data.type === DossDataFormat.FORMULA_STRING ||
        data.type === DossDataFormat.FORMULA_DATE ||
        data.type === DossDataFormat.FORMULA_DATE_TIME) &&
      data.data == null
    ) {
      return ''
    }

    if (!isResolvedTableCellData(data)) {
      throw new Error(`Invalid cell data for cell: ${JSON.stringify(cell.data)}`)
    }

    switch (data.type) {
      case DossDataFormat.ARRAY: {
        return joinSortedArrayElements(data.data)
      }
      case DossDataFormat.FORMULA:
      case DossDataFormat.FORMULA_NUMBER:
      case DossDataFormat.NUMBER: {
        if (header.formatPreset === FormatPreset.NUMBER_CURRENCY) {
          return formatCurrency(data.data, LocaleCodeMap[locale], currency)
        }
        return data.data.toString()
      }
      case DossDataFormat.DATE:
      case DossDataFormat.FORMULA_DATE: {
        return formatDate(data.data, { localeCode: LocaleCodeMap[locale] })
      }
      case DossDataFormat.DATE_TIME:
      case DossDataFormat.FORMULA_DATE_TIME: {
        return formatDateTimeByLocale(data.data, LocaleCodeMap[locale], timeZone)
      }
      case DossDataFormat.BOOLEAN:
      case DossDataFormat.ENUM:
      case DossDataFormat.FILE:
      case DossDataFormat.STRING:
      case DossDataFormat.FORMULA_STRING:
      case DossDataFormat.USER: {
        return data.data.toString()
      }
      case DossDataFormat.ID: {
        // Use the user visible value instead of the data because it already has the index
        // prefixes/suffixes applied
        return cell.userVisibleValue ?? ''
      }
      case DossDataFormat.LOOKUP: {
        // Check if this lookup column points to an ID column
        if (header.lookupColumn?.type === DataType.ID) {
          if (Array.isArray(data.data)) {
            return joinSortedArrayElements(
              data.data.map((value) =>
                getUserVisibleIndexValue(
                  value.toString(),
                  header.lookupColumn?.indexPrefix ?? '',
                  header.lookupColumn?.indexSuffix ?? '',
                ),
              ),
            )
          }
          // For lookup columns pointing to ID columns, apply the target column's prefix/suffix
          return cell.userVisibleValue ?? ''
        }

        // For non-ID lookup columns, use the original logic
        if (Array.isArray(data.data)) {
          return joinSortedArrayElements(data.data)
        }
        return data.data?.toString() ?? ''
      }
    }
  })
  return rowData
}

export interface BulkUpdateTableCell {
  tableColumnId: string
  data?: TableCellData
  lookupCellValues?: string[]
  documentIds?: string[]
  userIds?: string[]
  error?: string
}

export interface BulkUpdateTableRow {
  order: number
  cells: BulkUpdateTableCell[]
  hasError: boolean
}

export interface BulkUpdateTable {
  tableId: string
  rows: BulkUpdateTableRow[]
  numRecords: number
}

/**
 * Get the columns for csv upload, includes parent row id column for child tables
 */
export function getAllColumnsForCsvUpload(table: CsvTable, includeParentRowId = false): string[] {
  const headers: string[] = []
  // If the table is a child table, the first column in the csv should be the parent row id
  if (includeParentRowId) {
    headers.push('Parent Row ID')
  }

  headers.push(...getColumnsForCsvUpload(table.columns, 'Create').map((header) => header.name))
  return headers
}

/**
 * Creates a CSV with the table column headers only and downloads it
 */
export function createAndDownloadHeadersCsv(
  table: CsvTable,
  includeParentRowId = false,
  delimiter?: string,
): void {
  const headers = getAllColumnsForCsvUpload(table, includeParentRowId)
  const csv = Papa.unparse([headers], {
    header: false,
    // Use custom delimiter if provided
    delimiter: delimiter ?? undefined,
  })
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  FileSaver.saveAs(blob, `${table.name}_headers`, { autoBom: false })
}

/**
 * Validate that the csv header contains all the expected table columns
 */
function validateHeaders(
  headersRow: string[],
  columns: CsvColumn[],
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: TranslationKeys, options?: Record<string, any>) => string,
) {
  const columnNames = columns.map((column) => column.name.trim().toLowerCase())

  const isQuoteString = (str: string) => str.startsWith('"') && str.endsWith('"')
  if (headersRow.every(isQuoteString)) {
    headersRow = headersRow.map((header) => trim(header, '"').trim().toLowerCase())
  }

  // Create a frequency map to detect duplicate headers
  const headerCounts = headersRow.reduce<Record<string, number>>((counts, header) => {
    counts[header] = (counts[header] || 0) + 1
    return counts
  }, {})

  // Identify duplicate headers
  const duplicateHeaders = Object.keys(headerCounts).filter((header) => headerCounts[header] > 1)
  // Get the headers in the CSV that are not column names in the table
  const mismatchedHeaders = headersRow.filter((header) => !columnNames.includes(header))
  // Get the expected column names in table that are not present in the CSV
  const missingHeaders = columnNames.filter((columnName) => !headersRow.includes(columnName))

  if (duplicateHeaders.length > 0 || mismatchedHeaders.length > 0 || missingHeaders.length > 0) {
    let errorMessage = t('table.upload.headers_do_not_match')

    if (duplicateHeaders.length > 0) {
      errorMessage += t('table.upload.duplicate_headers', {
        duplicateHeaders: duplicateHeaders.join(', '),
      })
    }

    if (mismatchedHeaders.length > 0) {
      errorMessage += t('table.upload.unexpected_headers', {
        unexpectedHeaders: mismatchedHeaders.join(', '),
      })
    }

    if (missingHeaders.length > 0) {
      errorMessage += t('table.upload.missing_headers', {
        missingHeaders: missingHeaders.join(', '),
      })
    }

    throw new Error(errorMessage.trim())
  }
}

/**
 * Creates a mock column
 */
type MockTableColumnInput = {
  id?: string
  name?: string
  order?: number
  type?: DataType
  innerType?: DataType | null
  isNullable?: boolean
  enumOptions?: string[] | null
  indexType?: IndexType
  indexPrefix?: string
  indexSuffix?: string
  indexAutogenerate?: boolean
}

export function createMockTableColumn({
  id = uuidv4(),
  name = 'Header',
  order = 0,
  type = DataType.STRING,
  innerType = null,
  isNullable = true,
  enumOptions = null,
  indexType = IndexType.NONE,
  indexPrefix = '',
  indexSuffix = '',
  indexAutogenerate = false,
}: MockTableColumnInput): CsvColumn {
  return {
    id,
    name,
    order,
    type,
    innerType,
    isNullable,
    enumOptions,
    indexType,
    indexPrefix,
    indexSuffix,
    indexAutogenerate,
  }
}
/**
 * Parses the csv and sets the errors and validated rows
 */
export function parseUploadedCsvWithStream(
  table: CsvTable,
  readerEvent: ProgressEvent<FileReader>,
  setError: (error: string) => void,
  updateProgress: (rowCount: number) => void,
  setValidatedRows: (rows: BulkUpdateTableRow[]) => void,
  setIsParsing: (isParsing: boolean) => void,
  delimiter: string,
  dateLocale: string, // Locale in which dates are formatted
  numberLocale: string, // Locale in which numbers are formatted
  timeZone: string, // Timezone in which dates are formatted
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: TranslationKeys, options?: Record<string, any>) => string,
  parentRowId?: string,
) {
  let columns = getColumnsForCsvUpload(table.columns, 'Create')
  // Adjust columns if there's a parent table and no parent row ID was provided
  if (table.parentTable && !parentRowId) {
    const parentRowIdColumn = createMockTableColumn({ name: 'Parent Row ID', isNullable: false })
    columns = [parentRowIdColumn, ...columns]
  }
  const validatedRows: BulkUpdateTableRow[] = []
  let errorCount = 0
  let rowIndex = 1 // Initialize row index

  if (!readerEvent.target) {
    setError(t('table.upload.could_not_upload_csv'))
    return
  }

  Papa.parse<CsvRowData>(readerEvent.target.result as string, {
    header: true,
    // Using a worker will keep the page reactive
    worker: false,
    skipEmptyLines: true,
    // Use the delimiter provided by the user (defaults to the locale's delimiter)
    delimiter,
    quoteChar: '"',
    // Add transformHeader to trim whitespace from headers and make them all lowercase during
    // parsing to match how the csv is parsed on the backend
    transformHeader: (header) => header.trim().toLowerCase(),

    error: (error: PapaParseError) => {
      setError(t('table.upload.parsing_error', { error: error.message }))
    },
    beforeFirstChunk: (chunk) => {
      const firstNewlineIndex = chunk.indexOf('\n')
      const headerLine = firstNewlineIndex === -1 ? chunk : chunk.substring(0, firstNewlineIndex)
      const { data: headerRows } = Papa.parse<string[]>(headerLine.trim(), {
        delimiter,
        quoteChar: '"',
      })
      const headersRow = (headerRows[0] ?? [])
        // Normalize headers for comparison
        .map((header) => header.trim().toLowerCase())

      // Check if Button Nodes column is present in CSV headers (matches backend logic)
      const hasButtonNodeInCsv = headersRow.some((h) => {
        const normalized = h.toLowerCase().trim()
        return normalized === 'button node' || normalized === 'button nodes'
      })
      if (hasButtonNodeInCsv) {
        columns.push(createMockTableColumn({ name: BUTTON_NODES_COLUMN_NAME, isNullable: true }))
      }
      try {
        validateHeaders(headersRow, columns, t)
      } catch (error) {
        setError(error.message)
        return 'abort'
      }
      return chunk
    },
    step: (result, parser) => {
      const { data, errors } = result

      if (errors.length) {
        errorCount += errors.length
        setError(t('table.upload.errors_encountered', { errorCount }))
        parser.abort()
        return
      }

      const transformedRow = validateAndTransformRowData(
        data,
        columns,
        rowIndex++,
        dateLocale,
        numberLocale,
        timeZone,
      )

      validatedRows.push(transformedRow)
      updateProgress(validatedRows.length)

      if (validatedRows.length % 100 === 0) {
        setValidatedRows([...validatedRows])
      }
    },
    complete: () => {
      setValidatedRows([...validatedRows]) // Final update on completion
      setIsParsing(false)
    },
  })
}

/**
 * Validates and transforms a single CSV row data into structured data for bulk update.
 */
function validateAndTransformRowData(
  rowData: CsvRowData,
  columns: CsvColumn[],
  rowIndex: number,
  dateLocale: string, // Locale in which dates are formatted
  numberLocale: string, // Locale in which numbers are formatted
  timeZone: string, // Timezone in which dates are formatted
): BulkUpdateTableRow {
  const inputCells: BulkUpdateTableCell[] = []
  let rowHasError = false

  columns.forEach((column) => {
    const rawValue = rowData[column.name.trim().toLowerCase()] // Directly use the column name to access data
    let value: string | undefined

    if (typeof rawValue === 'number') {
      value = rawValue.toString()
    } else if (rawValue) {
      value = rawValue.trim()
    }
    const cellData = createCellData(column, value ?? '', dateLocale, numberLocale, timeZone)
    if (cellData.error) {
      rowHasError = true
    }

    inputCells.push(cellData)
  })

  return {
    order: rowIndex,
    cells: inputCells,
    hasError: rowHasError,
  }
}

/**
 * Create cell data based on column settings and value.
 */
function createCellData(
  column: CsvColumn,
  value: string,
  dateLocale: string, // Locale in which dates are formatted
  numberLocale: string, // Locale in which numbers are formatted
  timeZone: string, // Timezone in which dates are formatted
): BulkUpdateTableCell {
  if (!isLocaleCode(dateLocale)) {
    throw new Error(`Invalid date locale: ${dateLocale}`)
  }
  if (!isLocaleCode(numberLocale)) {
    throw new Error(`Invalid number locale: ${numberLocale}`)
  }

  let error: string | undefined
  let cellInput: CellInput | undefined
  try {
    cellInput = convertCsvInputToCell(value, column, {
      dateLocale,
      numberLocale,
      timeZone,
    })
  } catch (err) {
    error = err.message
  }

  return {
    ...cellInput,
    tableColumnId: column.id,
    error,
    data: cellInput?.data ?? undefined,
    lookupCellValues: cellInput?.lookupCellValues ?? [],
    documentIds: cellInput?.documentIds ?? [],
    userIds: cellInput?.userIds ?? [],
  }
}

/**
 * Given a CSV cell value and the column it should match, returns a Cell Input for it
 */
export function convertCsvInputToFormField(
  inputValue: string,
  field: DetailedFormField,
  context?: CsvConversionContext,
): FormFieldData {
  let value: ActionData | null = null
  // If the column accepts nullable values, and the input is empty, return an empty cell input
  if (inputValue !== '') {
    switch (field.type) {
      case FormFieldType.BOOLEAN: {
        if (!isBooleanString(inputValue)) {
          throw new Error(`Invalid boolean value "${inputValue}" for field ${field.name}`)
        }
        value = inputValue.toLowerCase() === 'true'
        break
      }
      case FormFieldType.DATE: {
        value = toDateTimeString(inputValue, {
          setToMidnightUTC: true,
          customFormat: context?.dateFormat, // Primary source for matching date format
          locale: context?.dateLocale, // Secondary source for matching date format
          isDateString: true,
        })
        break
      }
      case FormFieldType.DATE_TIME: {
        value = toDateTimeString(inputValue, {
          setToMidnightUTC: false,
          timeZone: context?.timeZone,
        })
        break
      }
      case FormFieldType.DROPDOWN: {
        if (field.lookupColumn) {
          throw new Error(
            `Cannot upload form field data for field ${field.name} because it is a lookup field`,
          )
        }
        if (!field.fieldOptions || !field.fieldOptions.includes(inputValue)) {
          throw new Error(
            `Invalid enum value "${inputValue}" for column ${field.name}. Not in ${JSON.stringify(
              field.fieldOptions,
            )}`,
          )
        }
        value = inputValue
        break
      }
      case FormFieldType.NUMBER: {
        if (context?.numberLocale) {
          value = parseNumberByLocale(inputValue, context.numberLocale)
        } else {
          value = parseNumberWithUnknownLocale(inputValue)
        }
        break
      }
      case FormFieldType.EMAIL:
      case FormFieldType.LONG_TEXT:
      case FormFieldType.SHORT_TEXT: {
        value = inputValue
        break
      }
      case FormFieldType.CHECKBOXES:
      case FormFieldType.FILE:
      case FormFieldType.LOCATION:
      case FormFieldType.RADIO_BUTTON:
      case FormFieldType.SIGNATURE:
        throw new Error(
          `Cannot upload form field data for field ${field.name} of type ${field.type}`,
        )
    }
  }

  return {
    name: field.name,
    order: field.order,
    type: field.type,
    isArray: field.isArray ?? false,
    isOverwritingLookup: false,
    value,
    formattedValue: inputValue === '' ? null : inputValue,
    lookupCellId: null,
    lookupRowId: null,
    lookupCellIds: null,
    lookupColumnId: null,
  }
}
